import { Link, useForm, usePage } from "@inertiajs/react";
import type { ClipboardEvent, FormEvent } from "react";
import AuthShell from "@/components/auth/AuthShell";
import FlashMessage from "@/components/FlashMessage";
import type { Flash } from "@/types";

type AuthTokensNewProps = {
  email?: string | null;
};

type SharedProps = {
  flash?: Flash;
};

export default function New({ email }: AuthTokensNewProps) {
  const { flash } = usePage<SharedProps>().props;
  const { data, processing, setData, post } = useForm({
    email: email ?? "",
    code: "",
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    post("/auth-token/verify");
  }

  function handleCodeChange(value: string) {
    setData("code", value.replace(/\D/g, "").slice(0, 6));
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    handleCodeChange(pasted);
  }

  return (
    <AuthShell>
      <FlashMessage flash={flash} />

      <div className="text-center">
        <h1 id="login-code-heading" className="mb-4 font-sans text-4xl font-bold text-gray-900">
          Enter your login code
        </h1>
        <p className="mb-6 text-lg text-gray-600">Check your email and enter the 6-digit code below</p>
      </div>

      <div className="card rounded-xl border border-gray-100 shadow-sm">
        <div className="card-body p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-code-form">
            <input id="email" name="email" type="hidden" value={data.email} readOnly />

            <div>
              <label htmlFor="code" className="field-label">
                6-digit login code
              </label>
              <input
                id="code"
                data-testid="login-code-input"
                className="field-input py-4 text-center font-mono text-2xl tracking-widest sm:text-xl"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                pattern="[0-9]{6}"
                placeholder="000000"
                required
                value={data.code}
                onChange={(event) => handleCodeChange(event.target.value)}
                onPaste={handlePaste}
              />
              <p className="mt-2 text-sm text-gray-500">Enter the 6-digit code from your email</p>
            </div>

            <button
              id="verify-code-submit"
              data-testid="verify-code-submit"
              className="btn btn-primary w-full py-3"
              disabled={processing}
              type="submit"
            >
              {processing ? "Verifying..." : "Verify Code"}
            </button>
          </form>

          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="text-center text-sm text-gray-500">
              <p className="mb-3">Didn&apos;t receive the email?</p>
              <Link
                href="/login"
                className="text-primary underline hover:text-primary-800"
                data-testid="try-different-email"
              >
                Try a different email address
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center text-sm">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-gray-25 px-4 font-body text-gray-500">Need help? Contact support</span>
          </div>
        </div>
      </div>
    </AuthShell>
  );
}
